/**
 * Prefetch + cache Terms CMS HTML for the register accordion.
 * Call on register form mount so expand never waits on the network for first paint.
 */
import {
  fetchCmsPages,
  findCmsPageByTitleOrMetaTitle,
  resolveCmsPageLocalizedHtml,
  type CmsAppLanguage,
} from '../data/domains/config';

type TermsCacheEntry = {
  language: CmsAppLanguage;
  html: string;
  status: 'idle' | 'loading' | 'ready' | 'error';
  promise: Promise<string> | null;
};

export type RegisterTermsCmsSnapshot = {
  language: CmsAppLanguage;
  html: string;
  status: TermsCacheEntry['status'];
  is_loading: boolean;
};

let cache: TermsCacheEntry = {
  language: 'en',
  html: '',
  status: 'idle',
  promise: null,
};

/** Stable snapshot ref for useSyncExternalStore — new object only when data changes. */
let snapshot: RegisterTermsCmsSnapshot = {
  language: cache.language,
  html: cache.html,
  status: cache.status,
  is_loading: false,
};

const listeners = new Set<() => void>();

function publish(): void {
  snapshot = {
    language: cache.language,
    html: cache.html,
    status: cache.status,
    is_loading: cache.status === 'loading',
  };
  listeners.forEach(fn => fn());
}

export function subscribeRegisterTermsCms(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Must return a cached object (same reference until publish). */
export function getRegisterTermsCmsSnapshot(): RegisterTermsCmsSnapshot {
  return snapshot;
}

/**
 * Start (or reuse) the pages fetch for the given language.
 * Safe to call repeatedly; in-flight promise is shared.
 */
export function prefetchRegisterTermsHtml(language: CmsAppLanguage): Promise<string> {
  if (cache.language === language && cache.status === 'ready') {
    return Promise.resolve(cache.html);
  }
  if (cache.language === language && cache.status === 'loading' && cache.promise != null) {
    return cache.promise;
  }

  const next_html = cache.language === language ? cache.html : '';
  cache = {
    language,
    html: next_html,
    status: 'loading',
    promise: null,
  };
  publish();

  const promise = (async () => {
    try {
      const pages = await fetchCmsPages();
      const tc_page = findCmsPageByTitleOrMetaTitle(pages, 'Terms & Conditions', 'terms');
      const html = tc_page ? resolveCmsPageLocalizedHtml(tc_page, language) : '';
      if (cache.language !== language) {
        return html;
      }
      cache = {
        language,
        html,
        status: 'ready',
        promise: null,
      };
      publish();
      return html;
    } catch {
      if (cache.language !== language) {
        return '';
      }
      cache = {
        language,
        html: '',
        status: 'error',
        promise: null,
      };
      publish();
      return '';
    }
  })();

  cache.promise = promise;
  return promise;
}
